import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { apiService } from '../services/api';
import { ValidationConfiguration } from './ValidationConfiguration';

interface FieldConfig {
  id: string;
  field: string;
  description: string;
  is_item: boolean;
  is_critical: boolean;
  is_manditory: boolean;
  manditory_region: string;
  manditory_invoice_type: string;
  manditory_invoice_category: string;
  vmd_ref_1: string;
  vmd_ref_2: string;
  vmd_ref_3: string;
  po_header_ref_1: string;
  po_header_ref_2: string;
  po_header_ref_3: string;
  po_line_ref_1: string;
  po_line_ref_2: string;
  po_line_ref_3: string;
  rule_1: string;
  rule_2: string;
  rule_3: string;
  derived_field_1: string;
  derived_field_2: string;
  derived_field_3: string;
  derived_field_4: string;
  derived_field_5: string;
  is_anomaly_detection: boolean;
  anomaly_definition: string;
  region: string;
  region_nuance: string;
  threshold: string;
  policy: string;
}

interface FeedbackData {
  id: string;
  feedback_data: any;
}

export const AdminPortal: React.FC = () => {
  // const { isDark, toggle } = useDarkMode();
  const [fieldConfig, setFieldConfig] = useState<FieldConfig[]>([]);
  const [feedbackData, setFeedbackData] = useState<FeedbackData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'field-config' | 'feedback' | 'validation-config'>('field-config');
  const [selectedScreenshot, setSelectedScreenshot] = useState<string | null>(null);
  const [learningModal, setLearningModal] = useState<{
    isOpen: boolean;
    isLoading: boolean;
    feedbackData?: any;
    learningResults?: any;
    editedDescription?: string;
  }>({ isOpen: false, isLoading: false });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [fieldConfigResponse, feedbackResponse] = await Promise.all([
        apiService.getFieldConfig(),
        apiService.getFeedbackData()
      ]);

      if (fieldConfigResponse.data) {
        setFieldConfig(fieldConfigResponse.data.field_config);
      }

      if (feedbackResponse.data) {
        setFeedbackData(feedbackResponse.data.feedback);
      }
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldConfigUpdate = async (updatedField: FieldConfig) => {
    try {
      const response = await apiService.updateFieldConfig(updatedField);
      if (response.error) {
        alert('Error updating field config: ' + response.error);
      } else {
        alert('Field config updated successfully!');
        setEditingField(null);
        loadData(); // Reload data
      }
    } catch (error) {
      console.error('Error updating field config:', error);
      alert('Error updating field config');
    }
  };

  const handleFieldChange = (fieldId: string, key: keyof FieldConfig, value: any) => {
    setFieldConfig(prev => prev.map(field => 
      field.id === fieldId 
        ? { ...field, [key]: value }
        : field
    ));
  };

  const handleViewScreenshot = (screenshotUrl: string) => {
    console.log('Screenshot URL clicked:', screenshotUrl);
    
    // Construct full URL if it's a relative path
    let fullUrl = screenshotUrl;
    if (screenshotUrl.startsWith('/api/')) {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
      fullUrl = `${API_BASE_URL}${screenshotUrl}`;
    }
    
    console.log('Full screenshot URL:', fullUrl);
    setSelectedScreenshot(fullUrl);
  };

  const closeScreenshotModal = () => {
    setSelectedScreenshot(null);
  };

  const handleLearnFromFeedback = async (feedbackData: any) => {
    setLearningModal({
      isOpen: true,
      isLoading: true,
      feedbackData
    });

    try {
      const response = await apiService.generateLearnings({
        fieldName: feedbackData.fieldName || feedbackData.field_name,
        originalValue: feedbackData.originalValue || feedbackData.original_value,
        modifiedValue: feedbackData.modifiedValue || feedbackData.modified_value,
        textFeedback: feedbackData.textFeedback || feedbackData.text_feedback || '',
        screenshotUrl: feedbackData.screenshotUrl || feedbackData.screenshot_url
      });

      if (response.error) {
        alert('Error generating learnings: ' + response.error);
        setLearningModal({ isOpen: false, isLoading: false });
      } else {
        setLearningModal({
          isOpen: true,
          isLoading: false,
          feedbackData,
          learningResults: response.data,
          editedDescription: response.data?.outputs.suggested_description || ''
        });
      }
    } catch (error) {
      console.error('Error generating learnings:', error);
      alert('Error generating learnings');
      setLearningModal({ isOpen: false, isLoading: false });
    }
  };

  const handleUpdateDescription = async () => {
    if (!learningModal.learningResults || !learningModal.editedDescription) return;

    try {
      const fieldConfigToUpdate = {
        ...fieldConfig.find(f => f.id === learningModal.learningResults.fieldConfigId),
        description: learningModal.editedDescription
      };

      const response = await apiService.updateFieldConfig(fieldConfigToUpdate);
      if (response.error) {
        alert('Error updating field description: ' + response.error);
      } else {
        alert('Field description updated successfully!');
        setLearningModal({ isOpen: false, isLoading: false });
        loadData(); // Reload data to show updates
      }
    } catch (error) {
      console.error('Error updating field description:', error);
      alert('Error updating field description');
    }
  };

  const closeLearningModal = () => {
    setLearningModal({ isOpen: false, isLoading: false });
  };

  const renderFieldConfigTable = () => (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse border border-gray-300 dark:border-gray-600 transition-colors duration-200">
        <thead>
          <tr className="bg-gray-100 dark:bg-gray-700 transition-colors duration-200">
            <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold transition-colors duration-200">Field</th>
            <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold transition-colors duration-200">Description</th>
            <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold transition-colors duration-200">Is Item</th>
            <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold transition-colors duration-200">Is Critical</th>
            <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold transition-colors duration-200">Is Mandatory</th>
            <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold transition-colors duration-200">Region</th>
            <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold transition-colors duration-200">Actions</th>
          </tr>
        </thead>
        <tbody>
          {fieldConfig.map((field) => (
            <tr key={field.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 transition-colors duration-200">
              <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100 transition-colors duration-200">
                {editingField === field.id ? (
                  <Input
                    value={field.field}
                    onChange={(e) => handleFieldChange(field.id, 'field', e.target.value)}
                    className="w-full"
                  />
                ) : (
                  <span className="font-medium">{field.field}</span>
                )}
              </td>
              <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100 transition-colors duration-200">
                {editingField === field.id ? (
                  <Input
                    value={field.description}
                    onChange={(e) => handleFieldChange(field.id, 'description', e.target.value)}
                    className="w-full"
                  />
                ) : (
                  <span>{field.description}</span>
                )}
              </td>
              <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100 transition-colors duration-200">
                {editingField === field.id ? (
                  <input
                    type="checkbox"
                    checked={field.is_item}
                    onChange={(e) => handleFieldChange(field.id, 'is_item', e.target.checked)}
                  />
                ) : (
                  <span>{field.is_item ? '✓' : '✗'}</span>
                )}
              </td>
              <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100 transition-colors duration-200">
                {editingField === field.id ? (
                  <input
                    type="checkbox"
                    checked={field.is_critical}
                    onChange={(e) => handleFieldChange(field.id, 'is_critical', e.target.checked)}
                  />
                ) : (
                  <span>{field.is_critical ? '✓' : '✗'}</span>
                )}
              </td>
              <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100 transition-colors duration-200">
                {editingField === field.id ? (
                  <input
                    type="checkbox"
                    checked={field.is_manditory}
                    onChange={(e) => handleFieldChange(field.id, 'is_manditory', e.target.checked)}
                  />
                ) : (
                  <span>{field.is_manditory ? '✓' : '✗'}</span>
                )}
              </td>
              <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100 transition-colors duration-200">
                {editingField === field.id ? (
                  <Input
                    value={field.region}
                    onChange={(e) => handleFieldChange(field.id, 'region', e.target.value)}
                    className="w-full"
                  />
                ) : (
                  <span>{field.region}</span>
                )}
              </td>
              <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100 transition-colors duration-200">
                {editingField === field.id ? (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleFieldConfigUpdate(field)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingField(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => setEditingField(field.id)}
                  >
                    Edit
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderFeedbackTable = () => {
    // Filter out feedback where originalValue is N/A (no user changes)
    const filteredFeedbackData = feedbackData.filter((feedback) => {
      const data = feedback.feedback_data;
      const originalValue = data?.originalValue || data?.original_value;
      return originalValue && originalValue !== 'N/A' && originalValue !== '';
    });

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600 transition-colors duration-200">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-700 transition-colors duration-200">
              <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold transition-colors duration-200">ID</th>
              <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold transition-colors duration-200">Field Name</th>
              <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold transition-colors duration-200">Original Value</th>
              <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold transition-colors duration-200">Modified Value</th>
              <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold transition-colors duration-200">Text Feedback</th>
              <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold transition-colors duration-200">Screenshot</th>
              <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold transition-colors duration-200">Timestamp</th>
              <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-gray-900 dark:text-gray-100 font-semibold transition-colors duration-200">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredFeedbackData.map((feedback) => {
              const data = feedback.feedback_data;
              return (
                <tr key={feedback.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 transition-colors duration-200">
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 transition-colors duration-200">{feedback.id}</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100 transition-colors duration-200">
                    {data?.fieldName || data?.field_name || 'N/A'}
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100 transition-colors duration-200">
                    {data?.originalValue || data?.original_value || 'N/A'}
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100 transition-colors duration-200">
                    {data?.modifiedValue || data?.modified_value || 'N/A'}
                  </td>
                  <td className="border border-gray-300 px-4 py-2 max-w-xs">
                    <div className="truncate" title={data?.textFeedback || data?.text_feedback}>
                      {data?.textFeedback || data?.text_feedback || 'N/A'}
                    </div>
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100 transition-colors duration-200">
                    {data?.screenshotUrl || data?.screenshot_url ? (
                      <button
                        onClick={() => handleViewScreenshot(data?.screenshotUrl || data?.screenshot_url)}
                        className="text-blue-600 hover:underline hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                      >
                        View
                      </button>
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-sm">
                    {data?.timestamp || data?.created_at || 'N/A'}
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100 transition-colors duration-200">
                    <Button
                      size="sm"
                      onClick={() => handleLearnFromFeedback(data)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      Learn
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredFeedbackData.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No feedback entries found where users made changes.
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Loading admin data...</div>
      </div>
    );
  }

  return (
    <div className="p-6 mx-auto bg-white dark:bg-gray-900 min-h-screen transition-colors duration-200">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2 transition-colors duration-200">Admin Portal</h1>
          <p className="text-gray-600 dark:text-gray-400 transition-colors duration-200">Manage configurations in the agents 🧠 </p>
        </div>
        {/* <Button
          onClick={toggle}
          variant="outline"
          size="sm"
          className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white border-purple-500 dark:border-purple-400 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-purple-500/25"
        >
          {isDark ? '☀️' : '🌙'} {isDark ? '' : ''}
        </Button> */}
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700 transition-colors duration-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('field-config')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'field-config'
                  ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              Field Configuration
            </button>
            <button
              onClick={() => setActiveTab('feedback')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'feedback'
                  ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              User Feedback
            </button>
            <button
              onClick={() => setActiveTab('validation-config')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'validation-config'
                  ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              Validation Configuration
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'field-config' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Field Configuration ({fieldConfig.length} fields)</span>
              <Button onClick={loadData} variant="outline">
                Refresh
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderFieldConfigTable()}
          </CardContent>
        </Card>
      )}

      {activeTab === 'feedback' && (() => {
        const filteredFeedbackData = feedbackData.filter((feedback) => {
          const data = feedback.feedback_data;
          const originalValue = data?.originalValue || data?.original_value;
          return originalValue && originalValue !== 'N/A' && originalValue !== '';
        });
        
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>User Feedback ({filteredFeedbackData.length} entries with changes)</span>
                <Button onClick={loadData} variant="outline">
                  Refresh
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {renderFeedbackTable()}
            </CardContent>
          </Card>
        );
      })()}

      {activeTab === 'validation-config' && (
        <ValidationConfiguration />
      )}

      {/* Screenshot Modal */}
      {selectedScreenshot && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={closeScreenshotModal}>
          <div className="relative max-w-4xl max-h-[90vh] bg-white rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-medium">Screenshot</h3>
              <button
                onClick={closeScreenshotModal}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            <div className="p-4">
              <div className="mb-2 text-sm text-gray-600">
                <strong>URL:</strong> {selectedScreenshot}
              </div>
              <img
                src={selectedScreenshot}
                alt="User feedback screenshot"
                className="max-w-full max-h-[70vh] object-contain mx-auto border"
                onLoad={() => console.log('Image loaded successfully:', selectedScreenshot)}
                onError={(e) => {
                  console.error('Image failed to load:', selectedScreenshot);
                  console.error('Error event:', e);
                  e.currentTarget.style.display = 'none';
                  const errorDiv = e.currentTarget.parentElement?.querySelector('#error-message') as HTMLElement;
                  if (errorDiv) {
                    errorDiv.style.display = 'block';
                  }
                }}
              />
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700" style={{display: 'none'}} id="error-message">
                Failed to load image. Please check if the file exists and the URL is correct.
              </div>
            </div>
            <div className="px-4 pb-4 text-sm text-gray-600">
              <p>Click outside the modal or the × button to close</p>
            </div>
          </div>
        </div>
      )}

      {/* Learning Modal */}
      {learningModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={closeLearningModal}>
          <div className="relative max-w-4xl max-h-[90vh] bg-white rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-medium">Generate Learnings</h3>
              <button
                onClick={closeLearningModal}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="p-6 max-h-[80vh] overflow-y-auto">
              {learningModal.isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-lg">Generating learnings...</div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Inputs Section */}
                  <div>
                    <h4 className="text-md font-medium mb-3 text-gray-900">Inputs</h4>
                    <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                      <div><strong>Field Name:</strong> {learningModal.learningResults?.inputs.fieldName}</div>
                      <div><strong>Current Description:</strong> {learningModal.learningResults?.inputs.currentDescription}</div>
                      <div><strong>Original Value:</strong> {learningModal.learningResults?.inputs.originalValue}</div>
                      <div><strong>Modified Value:</strong> {learningModal.learningResults?.inputs.modifiedValue}</div>
                      <div><strong>Text Feedback:</strong> {learningModal.learningResults?.inputs.textFeedback || 'No text feedback provided'}</div>
                      {learningModal.learningResults?.inputs.screenshotUrl && (
                        <div>
                          <strong>Screenshot:</strong> 
                          <button
                            onClick={() => handleViewScreenshot(learningModal.learningResults?.inputs.screenshotUrl!)}
                            className="ml-2 text-blue-600 hover:underline"
                          >
                            View Image
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Outputs Section */}
                  <div>
                    <h4 className="text-md font-medium mb-3 text-gray-900">AI Analysis</h4>
                    <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                      <div>
                        <strong>Diagnosis:</strong>
                        <p className="mt-1 text-gray-700">{learningModal.learningResults?.outputs.diagnosis}</p>
                      </div>
                      <div>
                        <strong>Suggested Description:</strong>
                        <p className="mt-1 text-gray-700">{learningModal.learningResults?.outputs.suggested_description}</p>
                      </div>
                    </div>
                  </div>

                  {/* Editable Description Section */}
                  <div>
                    <h4 className="text-md font-medium mb-3 text-gray-900">Edit Description</h4>
                    <textarea
                      value={learningModal.editedDescription || ''}
                      onChange={(e) => setLearningModal(prev => ({ ...prev, editedDescription: e.target.value }))}
                      className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Edit the field description..."
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-3 pt-4 border-t">
                    <Button variant="outline" onClick={closeLearningModal}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleUpdateDescription}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      disabled={!learningModal.editedDescription?.trim()}
                    >
                      Update Description
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};